// Import package components
import { createContext, useState } from "react";
import { useCookies } from "react-cookie";

import WorkOrderService from "../services/workorder.service";

export const WorkOrderContext = createContext();
export const WorkOrderProvider = ({ children }) => {
  const [isNew, setIsNew] = useState(false);
  const [workOrder, setWorkOrder] = useState(undefined);
  const [workOrders, setWorkOrders] = useState([]);
  const [attributes, setAttributes] = useState({
    tags: [],
    rentals: [],
    assigners: {
      recipients: [],
      contacts: [],
      assignees: [],
    },
  });
  const [stats, setStats] = useState({
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
    percent_completed: 0,
  });

  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setProcessing] = useState(false);

  const contextWorkOrder = {
    isNew,
    setIsNew,
    workOrder,
    setWorkOrder,
    total,
    workOrders,
    setWorkOrders,
    attributes,
    stats,
    loading,
    isProcessing,
    setProcessing,

    getAttributes: async (slug = undefined, types) => {
      setLoading(true);
      try {
        const resp = await WorkOrderService.getAttributes(slug, types);
        setLoading(false);
        setAttributes(resp.data);
        return resp.data;
      } catch (err) {
        console.log(err);
        setLoading(false);
      }
    },

    initWorkOrder: () => {
      setWorkOrder({
        account_id: 0,
        guest_reservation_id: 0,
        rental_id: 0,
        message_id: 0,
        created_by: 0,
        ticket_number: 0,
        status: "new",
        priority: "medium",
        summary: "",
        description: "",
      });
    },

    getWorkOrder: (id, slug = undefined) => {
      WorkOrderService.getWorkOrder(id, slug)
        .then((res, err) => {
          setWorkOrder(res.data);
          return res.data;
        })
        .catch((err) => {
          console.log(err);
        });
    },

    insertWorkOrder: async (obj) => {
      setProcessing(true);
      try {
        const res = await WorkOrderService.insertWorkOrder(obj);
        const newWorkOrder = res.data;
        
        if (newWorkOrder && newWorkOrder.checklist_instance) {
          newWorkOrder.checklist_item_count = 
            newWorkOrder.checklist_instance.ordered_item_ids?.length ?? null;
        }
        
        setWorkOrders((workOrders) => {
          const currentOrders = Array.isArray(workOrders) ? workOrders : [];
          return [newWorkOrder, ...currentOrders];
        });
        setProcessing(false);
        return newWorkOrder;
      } catch (err) {
        console.log(err);
        setProcessing(false);
      }
    },

    updateWorkOrder: async (id, obj) => {
      try {
        setProcessing(true);
        const res = await WorkOrderService.updateWorkOrder(id, obj);
        const newOrder = res.data;
        setWorkOrder({ ...workOrder, ...newOrder });
        // const idx = workOrders.findIndex(
        //   (order) => order.id.toString() === newOrder.id.toString()
        // );
        // console.log("idx:", idx);
        // if (idx > -1) {
        //   let updated = workOrders;
        //   updated[idx] = res.data;
        //   console.log("updated:", updated);
        //   setWorkOrders(updated);
        // }
        if (newOrder) {
          setWorkOrders((prev) =>
            prev.map((item) => {
              if (+item.id === +newOrder.id) {
                const checklist_item_count = 
                  newOrder.checklist_instance?.ordered_item_ids?.length ?? null;
                
                return { 
                  ...newOrder, 
                  work_order_number: item.work_order_number,
                  checklist_item_count,
                  // Ensure work_order_assignees is included from the updated response
                  work_order_assignees: newOrder.work_order_assignees || item.work_order_assignees
                };
              }
              return item;
            })
          );
        }
        setProcessing(false);
        return res.data;
      } catch (err) {
        setProcessing(false);
        console.log(err);
      }
    },

    deleteWorkOrder: async (id) => {
      setLoading(true);
      try {
        const res = await WorkOrderService.deleteWorkOrder(id);
        setLoading(false);
        setWorkOrders((orders) => orders.filter((order) => order.id !== id));
      } catch (err) {
        console.log(err);
        setLoading(false);
      }
    },

    getWorkOrders: (slug = undefined, criteria) => {
      setLoading(true);
      WorkOrderService.getWorkOrders(slug, criteria)
        .then((res) => {
          let { paginated, total } = res.data;
          setWorkOrders(paginated);
          setTotal(total);
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    },

    // getAssigners: (slug) =>
    //   WorkOrderService.getAssigners(slug)
    //     .then((res, err) => {
    //       setAssigners(res.data);
    //     })
    //     .catch((err) => {
    //       console.log(err);
    //     }),

    // getTags: (slug) =>
    //   WorkOrderService.getTags(slug)
    //     .then((res, err) => {
    //       setTags(res.data);
    //     })
    //     .catch((err) => {
    //       console.log(err);
    //     }),

    commentWorkOrder: (id, data, slug = undefined) => {
      setLoading(true);
      WorkOrderService.commentWorkOrder(id, data, slug)
        .then((res, err) => {
          setLoading(false);
          setWorkOrder(res.data);
          return res.data;
        })
        .catch((err) => {
          setLoading(false);
          console.log(err);
        });
    },

    completeChecklistItems: async (id, data, slug = undefined) => {
      try {
        const res = await WorkOrderService.completeChecklistItems(id, data, slug);
        if (res.data) {
          setWorkOrder(res.data);
        }
        return res;
      } catch (err) {
        console.log(err);
        throw err;
      }
    },

    updateCosts: async (id, data, slug = undefined) => {
      setProcessing(true);
      try {
        const res = await WorkOrderService.updateCosts(id, data, slug);
        if (res.data) {
          setWorkOrder({ ...workOrder, ...res.data });
        }
        setProcessing(false);
        return res.data;
      } catch (err) {
        console.log(err);
        setProcessing(false);
        throw err;
      }
    },

    getStats: async (params) => {
      setLoading(true);
      try {
        const res = await WorkOrderService.getStats(params);
        setStats(res.data);
        setLoading(false);
        return res.data;
      } catch (err) {
        console.log(err);
        setLoading(false);
        throw err;
      }
    },

    trackWorkOrder: async (id, data, slug = undefined) => {
      setProcessing(true);
      try {
        const res = await WorkOrderService.trackWorkOrder(id, data, slug);
        if (res.data) {
          setWorkOrder({ ...workOrder, ...res.data });
        }
        setProcessing(false);
        return res.data;
      } catch (err) {
        console.log(err);
        setProcessing(false);
        throw err;
      }
    },

    // ── Work Order Schedule CRUD ──

    getSchedule: async (workOrderId) => {
      try {
        const res = await WorkOrderService.getSchedule(workOrderId);
        return res.data;
      } catch (err) {
        // 404 means no schedule yet — that's fine, return null
        if (err?.response?.status === 404) return null;
        console.log(err);
        throw err;
      }
    },

    createSchedule: async (workOrderId, schedule) => {
      setProcessing(true);
      try {
        const res = await WorkOrderService.createSchedule(workOrderId, schedule);
        setProcessing(false);
        return res.data;
      } catch (err) {
        console.log(err);
        setProcessing(false);
        throw err;
      }
    },

    updateSchedule: async (workOrderId, schedule) => {
      setProcessing(true);
      try {
        const res = await WorkOrderService.updateSchedule(workOrderId, schedule);
        setProcessing(false);
        return res.data;
      } catch (err) {
        console.log(err);
        setProcessing(false);
        throw err;
      }
    },

    deleteSchedule: async (workOrderId) => {
      setProcessing(true);
      try {
        const res = await WorkOrderService.deleteSchedule(workOrderId);
        setProcessing(false);
        return res.data;
      } catch (err) {
        console.log(err);
        setProcessing(false);
        throw err;
      }
    },
  };

  return (
    <WorkOrderContext.Provider value={contextWorkOrder}>
      {children}
    </WorkOrderContext.Provider>
  );
};
